import React, { useEffect, useState, useRef } from "react";
import {
  Table,
  TableColumn,
  TableRow,
  TableCell,
  TableHeader,
  TableBody,
  Spinner,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Select,
  SelectItem,
  Input,
} from "@heroui/react";
import { BuildingDTO } from "shared/types/building";
import { getNumericValuesForBuilding } from "../operationalDashboard/utils/calculateNumericValuesForBuilding";
import { getNumericValuesForUnit } from "../operationalDashboard/utils/calculateNumericValuesForUnit";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  Checkmark,
  Eye,
  PlusCircle,
  Transfer,
} from "assets/icons";
import { PencilSimple, Garage, Car } from "@phosphor-icons/react";
import ColoredDot from "../operationalDashboard/components/coloredDot";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import ButtonNew from "components/buttonNew/buttonNew";
import CreateUnitsModal from "screens/acquisitions/modals/createUnitsModal";
import CreateAttributesModal from "screens/acquisitions/modals/createAttributesModal";
import { ClimatisationSource, HeatingConnectedTo } from "shared/types/climatisation";
import { EditableSelectComponent } from "components";

// Inline editable number cell component
interface EditableNumberCellProps {
  value: number | null | undefined;
  onSave: (value: number | null) => Promise<void>;
  placeholder?: string;
}

const EditableNumberCell: React.FC<EditableNumberCellProps> = ({
  value,
  onSave,
  placeholder = "0"
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value?.toString() || "");
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    setIsEditing(true);
    setEditValue(value?.toString() || "");
  };

  const handleSave = async () => {
    const newValue = editValue === "" ? null : parseInt(editValue, 10);
    if (newValue !== value) {
      setIsSaving(true);
      try {
        await onSave(newValue);
      } catch (error) {
        console.error("Error saving value:", error);
      } finally {
        setIsSaving(false);
      }
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      setIsEditing(false);
      setEditValue(value?.toString() || "");
    }
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  if (isSaving) {
    return <Spinner size="sm" />;
  }

  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        type="number"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        size="sm"
        className="w-20"
        classNames={{
          input: "text-center",
          inputWrapper: "min-h-unit-8 h-8"
        }}
      />
    );
  }

  return (
    <button
      onClick={handleClick}
      className="group flex items-center gap-1 px-2 py-1 rounded-sm border border-transparent hover:border-moss-primary/40 hover:bg-moss-primary/5 transition-all cursor-pointer min-w-[60px] justify-center"
    >
      <span>{value ?? placeholder}</span>
      <PencilSimple
        size={14}
        className="opacity-0 group-hover:opacity-100 text-moss-primary transition-opacity"
      />
    </button>
  );
};

// Inline editable text cell component for descriptions
interface EditableTextCellProps {
  value: string | null | undefined;
  onSave: (value: string | null) => Promise<void>;
  placeholder?: string;
  maxLength?: number;
}

const EditableTextCell: React.FC<EditableTextCellProps> = ({
  value,
  onSave,
  placeholder = "Geen beschrijving",
  maxLength = 2000
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || "");
  const [isSaving, setIsSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleClick = () => {
    setIsEditing(true);
    setEditValue(value || "");
  };

  const handleSave = async () => {
    const newValue = editValue.trim() === "" ? null : editValue.trim();
    if (newValue !== value) {
      setIsSaving(true);
      try {
        await onSave(newValue);
      } catch (error) {
        console.error("Error saving value:", error);
      } finally {
        setIsSaving(false);
      }
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsEditing(false);
      setEditValue(value || "");
    }
    // Allow Shift+Enter for new lines, Enter alone saves
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
  };

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [isEditing]);

  if (isSaving) {
    return <Spinner size="sm" />;
  }

  if (isEditing) {
    return (
      <div className="flex flex-col gap-2">
        <textarea
          ref={textareaRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          maxLength={maxLength}
          rows={4}
          className="w-full min-w-[300px] p-2 text-sm border border-moss-primary/40 rounded-md focus:outline-none focus:ring-2 focus:ring-moss-primary/50 resize-y"
          placeholder={placeholder}
        />
        <p className="text-xs text-neutral-secondary-mid">
          Druk op Enter om op te slaan, Shift+Enter voor nieuwe regel
        </p>
      </div>
    );
  }

  return (
    <button
      onClick={handleClick}
      className="group flex items-start gap-1 px-2 py-1 rounded-sm border border-transparent hover:border-moss-primary/40 hover:bg-moss-primary/5 transition-all cursor-pointer text-left w-full"
    >
      <span className="line-clamp-3 flex-1">{value || placeholder}</span>
      <PencilSimple
        size={14}
        className="opacity-0 group-hover:opacity-100 text-moss-primary transition-opacity shrink-0 mt-0.5"
      />
    </button>
  );
};

interface BuildingsTableProps {
  data: any[]; // Array of data objects
  fetchAcquisition: any;
  onCompleteAcquisition?: () => void;
}

const BuildingsTable: React.FC<BuildingsTableProps> = ({
  data,
  fetchAcquisition,
  onCompleteAcquisition,
}) => {
  const [enrichedData, setEnrichedData] = useState<any>([]);
  const { session } = useSession();
  const [transferring, setTransferring] = useState<{
    [buildingId: string]: boolean;
  }>({});
  const [isCreateUnitsModalOpen, setIsCreateUnitsModalOpen] = useState(false);
  const [selectedBuildingForUnits, setSelectedBuildingForUnits] =
    useState(null);
  const [isCreateAttributesModalOpen, setIsCreateAttributesModalOpen] = useState(false);
  const [selectedBuildingForAttributes, setSelectedBuildingForAttributes] =
    useState<BuildingDTO | null>(null);
  const [attributeTypeForModal, setAttributeTypeForModal] =
    useState<"parkings_inside" | "parkings_outside">("parkings_inside");
  // Track building attributes count for each building
  const [buildingAttributeCounts, setBuildingAttributeCounts] = useState<{
    [buildingId: string]: number;
  }>({});
  const [selectedBuildingForTransfer, setSelectedBuildingForTransfer] =
    useState<BuildingDTO | null>(null);
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<number | null>(null);
  const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure();

  useEffect(() => {
    const enrichBuildingsData = async () => {
      const enrichedBuildings = await Promise.all(
        data.map(async (building) => {
          const dueDiligenceDots = await getDueDiligenceDots(building);
          return { ...building, dueDiligenceDots };
        }),
      );
      setEnrichedData(enrichedBuildings);
    };

    if (data && data.length > 0) {
      enrichBuildingsData();
    }
  }, [data]); // Re-fetch when `data` changes

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/companies`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          },
          session,
        );
        const companiesData = await response.json();
        setCompanies(companiesData);
      } catch (error) {
        console.error("Error fetching companies:", error);
        toast.error("Fout bij het ophalen van bedrijven");
      }
    };

    fetchCompanies();
  }, [session]);

  // Fetch building attributes count for each building
  useEffect(() => {
    const fetchBuildingAttributes = async () => {
      if (!data || data.length === 0 || !session) return;

      const counts: { [buildingId: string]: number } = {};

      for (const building of data) {
        if (!building.id) continue;
        try {
          const response = await fetchWithAuth(
            `${API_URL}/building-attributes/building/${building.id}`,
            {
              method: "GET",
              headers: { "Content-Type": "application/json" },
            },
            session
          );
          if (response.ok) {
            const attributes = await response.json();
            counts[building.id] = Array.isArray(attributes) ? attributes.length : 0;
          }
        } catch (error) {
          console.error(`Error fetching attributes for building ${building.id}:`, error);
          counts[building.id] = 0;
        }
      }

      setBuildingAttributeCounts(counts);
    };

    fetchBuildingAttributes();
  }, [data, session]);

  const handleOpenCreateUnitsModal = (building: any) => {
    setSelectedBuildingForUnits(building);
    setIsCreateUnitsModalOpen(true);
  };
  const handleCloseCreateUnitsModal = () => {
    setSelectedBuildingForUnits(null);
    setIsCreateUnitsModalOpen(false);
    // Optionally refresh buildings data here
  };
  const handleOpenCreateAttributesModal = (
    building: BuildingDTO,
    attributeType: "parkings_inside" | "parkings_outside"
  ) => {
    setSelectedBuildingForAttributes(building);
    setAttributeTypeForModal(attributeType);
    setIsCreateAttributesModalOpen(true);
  };
  const handleCloseCreateAttributesModal = () => {
    setSelectedBuildingForAttributes(null);
    setIsCreateAttributesModalOpen(false);
  };
  const handleAttributesCreated = (buildingId: string, count: number) => {
    // Update the local count with newly created attributes
    setBuildingAttributeCounts((prev) => ({
      ...prev,
      [buildingId]: (prev[buildingId] || 0) + count,
    }));
    handleCloseCreateAttributesModal();
    fetchAcquisition();
  };

  // Helper function to check if parking attributes have been created
  const getParkingAttributeStatus = (building: BuildingDTO) => {
    const attributeCount = buildingAttributeCounts[building.id as string] || 0;
    const expectedInside = building.buildingAcquisitionInfo?.acquisitionFileNumParkingsInside || 0;
    const expectedOutside = building.buildingAcquisitionInfo?.acquisitionFileNumParkingsOutside || 0;

    // If we have attributes >= expected inside, inside is done
    // If we have attributes >= inside + outside, both are done
    return {
      insideCreated: attributeCount >= expectedInside && expectedInside > 0,
      outsideCreated: attributeCount >= (expectedInside + expectedOutside) && expectedOutside > 0,
    };
  };
  const handleTransferToOwned = async () => {
    if (!selectedBuildingForTransfer || !selectedBuildingForTransfer.id) return;

    if(!selectedBuildingForTransfer.units || selectedBuildingForTransfer.units.length === 0) {
      toast.error("Gebouw heeft geen units. Maak eerst units aan.");
      return;
    }

    if (!selectedCompany) {
      toast.error("Selecteer eerst een bedrijf.");
      return;
    }

    if (transferring[selectedBuildingForTransfer.id]) {
      // Early return if already transferring
      return;
    }

    // Set the building as transferring
    setTransferring((prev) => ({
      ...prev,
      [selectedBuildingForTransfer.id as string]: true,
    }));

    try {
      await fetchWithAuth(
        `${API_URL}/building/${selectedBuildingForTransfer.id}/acquire`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ companyId: selectedCompany }),
        },
        session,
      );
      toast.success("Gebouw overgezet naar aangekochte gebouwen.");
    } catch (e) {
      console.error(e);
      toast.error(
        "Er is een fout opgetreden bij het transferen van dit gebouw naar de aangekochte gebouwen.",
      );
    } finally {
      setTransferring((prev) => ({
        ...prev,
        [selectedBuildingForTransfer.id as string]: false,
      }));
      setSelectedBuildingForTransfer(null);
      setSelectedCompany(null);
      onClose();
      fetchAcquisition();
    }
  };
  const mergeDotValues = (accumulator: any, current: any) => {
    // Initialize any missing colors in the accumulator
    ['red', 'orange', 'green', 'transparent'].forEach((color) => {
      if (!accumulator[color]) {
        accumulator[color] = { count: 0, labels: [] };
      }
      if (!current[color]) {
        current[color] = { count: 0, labels: [] };
      }
    });

    // Now merge the values safely
    Object.keys(current).forEach((color) => {
      accumulator[color].count += current[color].count;
      accumulator[color].labels = [
        ...new Set([...accumulator[color].labels, ...current[color].labels]),
      ];
    });
    return accumulator;
  };
  const getDueDiligenceDots = (building: BuildingDTO) => {
    const buildingValues = getNumericValuesForBuilding(building, "Cumulative", session);
    const cumulativeUnitValuesPromises = (building.units as any).map(
      (unit: any) => getNumericValuesForUnit(unit, "Cumulative", session),
    );
    const getInitialDotValues = () => ({
      red: { count: 0, labels: [] },
      orange: { count: 0, labels: [] },
      green: { count: 0, labels: [] },
      transparent: { count: 0, labels: [] },
    });

    return Promise.all(cumulativeUnitValuesPromises).then((unitValuesArray) => {
      let cumulativeUnitValues = unitValuesArray.reduce(
        mergeDotValues,
        getInitialDotValues(),
      );
      console.log(building.id);
      console.log(cumulativeUnitValues);
      let totalValues = mergeDotValues(buildingValues, cumulativeUnitValues);
      return totalValues;
    });
  };
  const handleClimatisationSourceChange = async (buildingId: string, value: ClimatisationSource) => {
    try {
      const building = data.find(b => b.id === buildingId);
      if (!building) return;

      const patchObject = {
        id: building.climatisationInfo?.id,
        source: value,
        buildingId: buildingId
      };

      await fetchWithAuth(
        `${API_URL}/climatisation/info`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(patchObject),
        },
        session
      );

      // Update local state
      setEnrichedData((prevData: any) => 
        prevData.map((b: any) => {
          if (b.id === buildingId) {
            return {
              ...b,
              climatisationInfo: {
                ...b.climatisationInfo,
                source: value
              }
            };
          }
          return b;
        })
      );

      toast.success("Klimatisatie bron succesvol bijgewerkt");
    } catch (error) {
      console.error(error);
      toast.error("Er is een fout opgetreden bij het bijwerken van de klimatisatie bron");
    }
  };
  const handleClimatisationConnectedToChange = async (buildingId: string, value: HeatingConnectedTo) => {
    try {
      const building = data.find(b => b.id === buildingId);
      if (!building) return;

      const patchObject = {
        id: building.climatisationInfo?.id,
        connectedTo: value,
        buildingId: buildingId
      };

      await fetchWithAuth(
        `${API_URL}/climatisation/info`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(patchObject),
        },
        session
      );

      // Update local state
      setEnrichedData((prevData: any) =>
        prevData.map((b: any) => {
          if (b.id === buildingId) {
            return {
              ...b,
              climatisationInfo: {
                ...b.climatisationInfo,
                connectedTo: value
              }
            };
          }
          return b;
        })
      );

      toast.success("Klimatisatie aansluiting succesvol bijgewerkt");
    } catch (error) {
      console.error(error);
      toast.error("Er is een fout opgetreden bij het bijwerken van de klimatisatie aansluiting");
    }
  };

  const handleBuildingAcquisitionInfoUpdate = async (
    buildingId: string,
    field: string,
    value: number | string | null
  ) => {
    try {
      const building = enrichedData.find((b: any) => b.id === buildingId);
      if (!building) return;

      const patchObject = {
        id: building.buildingAcquisitionInfo?.id,
        buildingId: buildingId,
        [field]: value
      };

      await fetchWithAuth(
        `${API_URL}/building/building-acquisition-info`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(patchObject),
        },
        session
      );

      // Update local state
      setEnrichedData((prevData: any) =>
        prevData.map((b: any) => {
          if (b.id === buildingId) {
            return {
              ...b,
              buildingAcquisitionInfo: {
                ...b.buildingAcquisitionInfo,
                [field]: value
              }
            };
          }
          return b;
        })
      );

      toast.success("Gegevens succesvol bijgewerkt");
    } catch (error) {
      console.error("Error updating building acquisition info:", error);
      toast.error("Er is een fout opgetreden bij het bijwerken van de gegevens");
      throw error;
    }
  };

  return (
    <>
      <Table
        //selectionMode="multiple"
        aria-label="Example static collection table"
        classNames={{
          wrapper: "shadow-none rounded-none p-0",
          thead: "h-fit border-b-2 border-neutral-primary/40",
          th: "rounded-none! bg-white py-2 h-fit text-sm text-neutral-primary font-medium",
          tbody: "border-b divide-y divide-neutral-primary/15",
          tr: "divide-x divide-neutral-primary/15",
          td: "py-2.5",
        }}
      >
        <TableHeader>
          <TableColumn key="Gebouwcode">Gebouwcode</TableColumn>
          <TableColumn key="Adres">Adres</TableColumn>
          <TableColumn key="Due diligence">Due Diligence</TableColumn>
          <TableColumn key="Res units"># residentiële units</TableColumn>
          <TableColumn key="Comm units">
            <div className="flex flex-col">
              <span># commerciële units</span>
              <span className="text-xs text-neutral-secondary-mid font-normal">(retail + kantoren)</span>
            </div>
          </TableColumn>
          <TableColumn key="Parkeerplaatsen">
            <div className="flex flex-col">
              <span># parkeerplaatsen</span>
              <span className="text-xs text-neutral-secondary-mid font-normal">(binnen + buiten)</span>
            </div>
          </TableColumn>
          <TableColumn key="KlimatisatieAansluiting">Verwarming</TableColumn>
          <TableColumn key="KlimatisatieBron">Type verwarming</TableColumn>
          <TableColumn key="Units sharepoint">
            # units aangemaakt op sharepoint
          </TableColumn>
          <TableColumn key="Beschrijving">Beschrijving</TableColumn>
          <TableColumn key="Detail">Detail</TableColumn>
          <TableColumn key="Convert">Zet om naar aangekocht</TableColumn>
        </TableHeader>
        <TableBody emptyContent={
          <div className="flex flex-col items-center gap-4 py-8">
            <p className="text-neutral-secondary-mid">Geen gebouwen gevonden</p>
            {onCompleteAcquisition && (
              <ButtonNew
                variant="primaryMoss"
                icon={<PencilSimple weight="duotone" />}
                label="Vervolledig dossier"
                onClick={onCompleteAcquisition}
              />
            )}
          </div>
        }>
          {enrichedData &&
            enrichedData.map((building: BuildingDTO, i: number) => (
              <TableRow key={i}>
                <TableCell>{building.id}</TableCell>
                <TableCell>{building.googlePlaceLongAddress}</TableCell>
                {/* Render due diligence dots here; you may need to adjust based on how you want them displayed */}
                <TableCell>
                  <div className="flex cursor-pointer flex-wrap items-center justify-center gap-2 rounded-lg border border-neutral-primary/10 p-2">
                    <ColoredDot
                      color="green"
                      number={
                        (building as any).dueDiligenceDots.green.count || 0
                      }
                      labels={
                        (building as any).dueDiligenceDots.green.labels || []
                      }
                    />
                    <ColoredDot
                      color="orange"
                      number={
                        (building as any).dueDiligenceDots.orange.count || 0
                      }
                      labels={
                        (building as any).dueDiligenceDots.orange.labels || []
                      }
                    />
                    <ColoredDot
                      color="red"
                      number={(building as any).dueDiligenceDots.red.count || 0}
                      labels={
                        (building as any).dueDiligenceDots.red.labels || []
                      }
                    />
                    <ColoredDot
                      color="transparent"
                      number={
                        (building as any).dueDiligenceDots.transparent.count ||
                        0
                      }
                      labels={
                        (building as any).dueDiligenceDots.transparent.labels ||
                        []
                      }
                    />
                  </div>
                </TableCell>
                <TableCell>
                  <EditableNumberCell
                    value={building.buildingAcquisitionInfo?.acquisitionFileNumResidentialUnits}
                    onSave={(value) =>
                      handleBuildingAcquisitionInfoUpdate(
                        building.id as string,
                        "acquisitionFileNumResidentialUnits",
                        value
                      )
                    }
                    placeholder="0"
                  />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <EditableNumberCell
                      value={building.buildingAcquisitionInfo?.acquisitionFileNumRetailUnits}
                      onSave={(value) =>
                        handleBuildingAcquisitionInfoUpdate(
                          building.id as string,
                          "acquisitionFileNumRetailUnits",
                          value
                        )
                      }
                      placeholder="0"
                    />
                    <span className="text-neutral-secondary-mid">+</span>
                    <EditableNumberCell
                      value={building.buildingAcquisitionInfo?.acquisitionFileNumOfficeUnits}
                      onSave={(value) =>
                        handleBuildingAcquisitionInfoUpdate(
                          building.id as string,
                          "acquisitionFileNumOfficeUnits",
                          value
                        )
                      }
                      placeholder="0"
                    />
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-2">
                    {/* Parking counts row */}
                    <div className="flex items-center gap-1">
                      <EditableNumberCell
                        value={building.buildingAcquisitionInfo?.acquisitionFileNumParkingsInside}
                        onSave={(value) =>
                          handleBuildingAcquisitionInfoUpdate(
                            building.id as string,
                            "acquisitionFileNumParkingsInside",
                            value
                          )
                        }
                        placeholder="0"
                      />
                      <span className="text-neutral-secondary-mid">+</span>
                      <EditableNumberCell
                        value={building.buildingAcquisitionInfo?.acquisitionFileNumParkingsOutside}
                        onSave={(value) =>
                          handleBuildingAcquisitionInfoUpdate(
                            building.id as string,
                            "acquisitionFileNumParkingsOutside",
                            value
                          )
                        }
                        placeholder="0"
                      />
                    </div>
                    {/* Attribute creation buttons */}
                    {(() => {
                      const parkingStatus = getParkingAttributeStatus(building);
                      const expectedInside = building.buildingAcquisitionInfo?.acquisitionFileNumParkingsInside ?? 0;
                      const expectedOutside = building.buildingAcquisitionInfo?.acquisitionFileNumParkingsOutside ?? 0;

                      return (
                        <div className="flex flex-col gap-1">
                          {expectedInside > 0 && (
                            parkingStatus.insideCreated ? (
                              <div className="flex items-center gap-2 text-xs text-moss-primary">
                                <Checkmark className="h-4 w-4" />
                                <span>Binnenparking attributen aangemaakt</span>
                              </div>
                            ) : (
                              <ButtonNew
                                icon={<Garage weight="duotone" />}
                                label="Maak binnenparking attributen"
                                variant="secondaryMoss"
                                onClick={() => handleOpenCreateAttributesModal(building, "parkings_inside")}
                              />
                            )
                          )}
                          {expectedOutside > 0 && (
                            parkingStatus.outsideCreated ? (
                              <div className="flex items-center gap-2 text-xs text-moss-primary">
                                <Checkmark className="h-4 w-4" />
                                <span>Buitenparking attributen aangemaakt</span>
                              </div>
                            ) : (
                              <ButtonNew
                                icon={<Car weight="duotone" />}
                                label="Maak buitenparking attributen"
                                variant="secondaryMoss"
                                onClick={() => handleOpenCreateAttributesModal(building, "parkings_outside")}
                              />
                            )
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </TableCell>
                <TableCell className="min-w-[200px]">
                  <EditableSelectComponent
                    label="Verwarming"
                    value={building.climatisationInfo?.connectedTo === 'Eigen systeem' ? 'Privatief systeem' : 
                           building.climatisationInfo?.connectedTo === 'Centraal systeem' ? 'Collectief systeem' : 
                           building.climatisationInfo?.connectedTo || HeatingConnectedTo.UNKNOWN}
                    options={Object.values(HeatingConnectedTo).map(value => ({ 
                      label: value === 'Eigen systeem' ? 'Privatief systeem' :
                             value === 'Centraal systeem' ? 'Collectief systeem' : 
                             value,
                      value 
                    }))}
                    editMode={true}
                    onChange={(value) => handleClimatisationConnectedToChange(building.id as string, value as HeatingConnectedTo)}
                  />
                </TableCell>
                <TableCell className="min-w-[200px]">
                  <EditableSelectComponent
                    label="Type verwarming"
                    value={building.climatisationInfo?.source || ClimatisationSource.UNKNOWN}
                    options={Object.values(ClimatisationSource).map(value => ({ 
                      label: value, 
                      value 
                    }))}
                    editMode={true}
                    onChange={(value) => handleClimatisationSourceChange(building.id as string, value as ClimatisationSource)}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {building?.units?.length || 0}
                    {(!building?.units?.length ||
                      building?.units?.length == 0) && (
                      <ButtonNew
                        icon={<PlusCircle />}
                        label="Maak mappen voor units"
                        onClick={() => handleOpenCreateUnitsModal(building)}
                      />
                    )}
                  </div>
                </TableCell>
                <TableCell className="max-w-[400px] min-w-[250px]">
                  <EditableTextCell
                    value={building.buildingAcquisitionInfo?.acquisitionFileBuildingDescription}
                    onSave={(value) =>
                      handleBuildingAcquisitionInfoUpdate(
                        building.id as string,
                        "acquisitionFileBuildingDescription",
                        value
                      )
                    }
                    placeholder="Geen beschrijving"
                  />
                </TableCell>
                <TableCell>
                  <Link to={`/building/${building.id}`}>
                    <Eye className="h-[18px] w-[18px]" />
                  </Link>
                </TableCell>
                <TableCell>
                  {building.owner ? (
                    <Checkmark />
                  ) : transferring[building.id as string] ? (
                    (<Spinner size="sm" />) // Show spinner when transferring
                  ) : (
                    <Transfer
                      className="h-[18px] w-[18px] cursor-pointer"
                      onClick={() => {
                        setSelectedBuildingForTransfer(building);
                        onOpen();
                      }}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      {selectedBuildingForUnits && (
        <CreateUnitsModal
          isOpen={isCreateUnitsModalOpen}
          onClose={handleCloseCreateUnitsModal}
          building={selectedBuildingForUnits}
          onUnitsCreated={() => {
            // Handle any actions needed after units are successfully created
            handleCloseCreateUnitsModal();
            fetchAcquisition();
            // Optionally refresh the buildings list to show the new unit count
          }}
        />
      )}
      {selectedBuildingForAttributes && (
        <CreateAttributesModal
          isOpen={isCreateAttributesModalOpen}
          onClose={handleCloseCreateAttributesModal}
          building={selectedBuildingForAttributes}
          attributeType={attributeTypeForModal}
          onAttributesCreated={(createdCount?: number) => {
            const count = createdCount ?? (
              attributeTypeForModal === "parkings_inside"
                ? selectedBuildingForAttributes.buildingAcquisitionInfo?.acquisitionFileNumParkingsInside ?? 0
                : selectedBuildingForAttributes.buildingAcquisitionInfo?.acquisitionFileNumParkingsOutside ?? 0
            );
            handleAttributesCreated(
              selectedBuildingForAttributes.id as string,
              count
            );
          }}
        />
      )}
      <Modal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        isDismissable={true}
        isKeyboardDismissDisabled={true}
        className="font-normal text-neutral-primary"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <p className="flex items-center gap-1 text-sm font-medium text-status-bad">
                  <AlertCircle className="h-[17px] w-[17px]" /> Zet gebouw om
                  naar aangekocht
                </p>
              </ModalHeader>
              <ModalBody>
                <p>
                  Weet je zeker dat je dit gebouw wilt omzetten naar aangekocht?
                  Dit kan niet ongedaan worden gemaakt.
                </p>

                <Select
                  label="Selecteer bedrijf"
                  placeholder="Kies het bedrijf waar dit gebouw onder valt"
                  selectedKeys={selectedCompany ? [selectedCompany.toString()] : []}
                  onSelectionChange={(keys) => {
                    const selectedKey = Array.from(keys)[0];
                    setSelectedCompany(selectedKey ? Number(selectedKey) : null);
                  }}
                  className="mb-4"
                  isRequired
                >
                  {companies.map((company) => (
                    <SelectItem key={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </Select>

                <ul>
                  <li>
                    - Het gebouw zal te zien zijn onder het menu "Gebouwen" in
                    de Yallhub
                  </li>
                  <br />
                  <li>
                    - Op SharePoint zal de gebouwfolder van "
                    {selectedBuildingForTransfer?.id}" worden verplaatst van de
                    map "Acquisities" naar de map "Portefeuille"
                  </li>
                  <br />
                  <li>
                    - De financials uit de acquisitie excel zullen overgenomen
                    worden onder het gebouw onder het tabje "financials". Indien
                    deze portefeuille uit meerdere gebouwen bestaat, moeten de
                    financials manueel gesplitst worden per gebouw.
                  </li>
                  <br />
                  <li>
                    - Het gebouw en de units zullen automatisch aangemaakt worden
                    in Odoo als kostenplaatsen voor financiële rapportage.
                  </li>
                </ul>

                {transferring[selectedBuildingForTransfer?.id as string] && (
                  <div className="mt-4 p-4 bg-secondary/5 rounded-lg border border-primary/20">
                    <div className="flex items-center gap-3">
                      <div className="text-2xl">☕</div>
                      <div>
                        <p className="font-medium text-neutral-primary">
                          Dit kan enkele minuten duren...
                        </p>
                        <p className="text-sm text-neutral-secondary-mid">
                          Ga een koffie halen! Het gebouw wordt automatisch overgezet en in Odoo aangemaakt.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </ModalBody>
              <ModalFooter className="justify-start">
                <ButtonNew
                  variant="primaryStatusRed"
                  onClick={handleTransferToOwned}
                  icon={transferring[selectedBuildingForTransfer?.id as string] ? <Spinner size="sm" /> : <Transfer />}
                  label={transferring[selectedBuildingForTransfer?.id as string] ? "Bezig met omzetten..." : "Zet gebouw om naar aangekocht"}
                  isDisabled={transferring[selectedBuildingForTransfer?.id as string]}
                />
                <ButtonNew
                  variant="secondaryNeutral"
                  onClick={onClose}
                  label={"Nee, toch niet"}
                  isDisabled={transferring[selectedBuildingForTransfer?.id as string]}
                />
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default BuildingsTable;
